import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  useAdminNotifications,
  useMarkNotificationRead,
  useUnreadNotificationCount,
} from '../features/notifications/hooks/useAdminNotifications';
import type { AdminNotification } from '../types/admin';
import { Icon } from './Icon';
import styles from './NotificationBell.module.scss';

const BELL_PAGE_SIZE = 10;

function formatRelativeTime(iso: string, locale: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diffMs = Date.now() - date.getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return t('notifications.justNow');
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return t('notifications.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('notifications.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 7) return t('notifications.daysAgo', { count: days });
  return date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
}

function buildBadgeText(count: number): string {
  if (count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
}

function destinationFor(notification: AdminNotification): string | null {
  if (notification.category === 'final_result_submitted' && notification.related_patient_id) {
    const cycle = notification.related_cycle_id ? `?result_cycle=${encodeURIComponent(notification.related_cycle_id)}` : '';
    return `/clients/${notification.related_patient_id}${cycle}#result`;
  }
  if (notification.related_chat_message_id && notification.related_patient_id) {
    return `/chats?patient=${notification.related_patient_id}`;
  }
  if (notification.related_payment_id && notification.related_patient_id) {
    return `/clients/${notification.related_patient_id}`;
  }
  if (notification.related_patient_id) {
    return `/clients/${notification.related_patient_id}`;
  }
  return null;
}

export function NotificationBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);

  const unreadQuery = useUnreadNotificationCount();
  const listQuery = useAdminNotifications({ page: 1, page_size: BELL_PAGE_SIZE });
  const markRead = useMarkNotificationRead();

  const unreadCount = unreadQuery.data?.unread_count ?? 0;
  const badge = buildBadgeText(unreadCount);

  useEffect(() => {
    if (!open) return undefined;
    function handleClick(event: MouseEvent) {
      if (!wrapperRef.current) return;
      if (event.target instanceof Node && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    }
    function handleKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  function handleRowClick(notification: AdminNotification) {
    setOpen(false);
    if (!notification.read_at) {
      markRead.mutate(notification.id);
    }
    const dest = destinationFor(notification);
    if (dest) navigate(dest);
  }

  function handleViewAll() {
    setOpen(false);
    navigate('/notifications');
  }

  const items = listQuery.data?.items ?? [];

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={t('notifications.bellTitle')}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((prev) => !prev)}
      >
        <Icon name="bell" size={18} />
        {badge ? <span className={styles.badge}>{badge}</span> : null}
      </button>

      {open ? (
        <div className={styles.panel} role="menu">
          <div className={styles.panelHeader}>
            <span>{t('notifications.bellTitle')}</span>
          </div>
          <div className={styles.list}>
            {listQuery.isLoading ? (
              <div className={styles.loading}>{t('notifications.loading')}</div>
            ) : items.length === 0 ? (
              <div className={styles.empty}>{t('notifications.noNew')}</div>
            ) : (
              items.map((notification) => {
                const isUnread = notification.read_at == null;
                const rowClass = `${styles.row} ${isUnread ? styles.rowUnread : ''}`.trim();
                const titleClass = `${styles.rowTitle} ${isUnread ? styles.rowTitleUnread : ''}`.trim();
                return (
                  <button
                    type="button"
                    key={notification.id}
                    className={rowClass}
                    onClick={() => handleRowClick(notification)}
                  >
                    <span className={titleClass}>{notification.title}</span>
                    {notification.body ? (
                      <span className={styles.rowBody}>{notification.body}</span>
                    ) : null}
                    <span className={styles.rowMeta}>
                      {formatRelativeTime(notification.created_at, i18n.language, t)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.footer}>
            <button type="button" className={styles.footerLink} onClick={handleViewAll}>
              {t('notifications.viewAll')}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
